import styled, { keyframes } from "styled-components";
import logo from "../assets/fullLogo2.PNG";
import { FaInstagram, FaTiktok, FaTwitter, FaLinkedinIn } from "react-icons/fa";

// ── Keyframes
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50%       { opacity: 1; }
`;

// ── Footer shell
const FooterEl = styled.footer`
  position: relative;
  background: #020006;
  border-top: 1px solid rgba(168, 85, 247, 0.1);
  overflow: hidden;
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.025;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(130px);
  pointer-events: none;
  opacity: 0.12;
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  bottom: ${({ bottom }) => bottom};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 clamp(1.5rem, 5vw, 4rem);
`;

// ── Top CTA band
const CtaBand = styled.div`
  padding: 80px 0 72px;
  text-align: center;
  border-bottom: 1px solid rgba(168, 85, 247, 0.08);
`;

const CtaEyebrow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1.2rem;
`;

const CtaHeadline = styled.h2`
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(2.6rem, 6vw, 5rem);
  font-weight: 300;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 2.2rem;

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }
`;

const AppStoreBtns = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StoreBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 22px;
  height: 52px;
  border-radius: 14px;
  text-decoration: none;
  cursor: pointer;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
  }
`;

const AppStorePrimary = styled(StoreBtn)`
  background: #fff;
  box-shadow: 0 8px 30px rgba(255, 255, 255, 0.08);

  &:hover {
    box-shadow: 0 16px 40px rgba(255, 255, 255, 0.15);
  }
`;

const AppStoreSecondary = styled(StoreBtn)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
  }
`;

const StoreBtnIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StoreBtnCopy = styled.div`
  display: flex;
  flex-direction: column;
`;

const StoreBtnTop = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.06em;
  line-height: 1;
  color: ${({ dark }) => (dark ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.45)")};
`;

const StoreBtnBottom = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ dark }) => (dark ? "#000" : "rgba(255,255,255,0.9)")};
  letter-spacing: 0.01em;
`;

// ── Main footer body
const FooterBody = styled.div`
  padding: 60px 0 48px;
  display: grid;
  grid-template-columns: 1.8fr repeat(3, 1fr);
  gap: clamp(2rem, 4vw, 4rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.08);

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

// Brand col
const BrandCol = styled.div``;

const LogoImg = styled.img`
  height: 28px;
  width: auto;
  object-fit: contain;
  display: block;
  margin-bottom: 1.2rem;
  opacity: 0.9;
`;

const BrandTagline = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.3);
  line-height: 1.75;
  max-width: 240px;
  margin-bottom: 1.8rem;
`;

const SocialsRow = styled.div`
  display: flex;
  gap: 0.7rem;
`;

const SocialBtn = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  text-decoration: none;
  transition:
    background 0.2s,
    border-color 0.2s,
    color 0.2s,
    transform 0.2s;

  &:hover {
    background: rgba(168, 85, 247, 0.12);
    border-color: rgba(168, 85, 247, 0.35);
    color: #c084fc;
    transform: translateY(-2px);
  }
`;

// Nav col
const NavCol = styled.div``;

const NavColTitle = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.22);
  margin-bottom: 1.1rem;
`;

const NavList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const NavItem = styled.li``;

const NavLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  text-decoration: none;
  transition: color 0.2s ease;
  display: inline-block;

  &:hover {
    color: rgba(255, 255, 255, 0.85);
  }
`;

// ── Bottom bar
const BottomBar = styled.div`
  padding: 22px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Copyright = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.2);
  letter-spacing: 0.02em;
`;

const LegalLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const LegalLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.2);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.55);
  }
`;

const MadeWith = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.15);

  span {
    color: #a855f7;
    animation: ${pulse} 2s ease-in-out infinite;
    display: inline-block;
  }
`;

// ── Data
const NAV_COLS = [
  {
    title: "Product",
    links: [
      { label: "How it Works", href: "#how-it-works" },
      { label: "Features", href: "#features" },
      { label: "Safety", href: "#safety" },
      { label: "Download", href: "#download" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Blog", href: "/blog" },
      { label: "Careers", href: "/careers" },
      { label: "Press", href: "/press" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "FAQ", href: "#faq" },
      { label: "Contact", href: "#contact" },
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Use", href: "/terms" },
    ],
  },
];

const SOCIALS = [
  { icon: <FaInstagram />, href: "https://instagram.com", label: "Instagram" },
  { icon: <FaTiktok />, href: "https://tiktok.com", label: "TikTok" },
  { icon: <FaTwitter />, href: "https://twitter.com", label: "Twitter" },
  { icon: <FaLinkedinIn />, href: "https://linkedin.com", label: "LinkedIn" },
];

// Apple SVG
const AppleSvg = () => (
  <svg
    viewBox="0 0 814 1000"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 135.4-317.3 268.9-317.3 70.2 0 128.6 46.4 173 46.4 42.4 0 109.1-49.1 186.3-49.1 30.4 0 110.2 3.9 162.7 69.2zm-85.5-220.5c39.5-46.4 67.8-110.9 67.8-175.4 0-8.9-.6-17.8-2.2-25.4-64.4 2.6-140.5 43.7-185.5 93.8-35.8 39.5-69.7 103.4-69.7 168.5 0 9.5 1.6 19 2.2 22.3 3.9.6 10.4 1.6 16.9 1.6 57.7 0 129.4-39.5 170.5-85.4z" />
  </svg>
);

// Google Play SVG
const PlaySvg = () => (
  <svg
    viewBox="0 0 512 512"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M48 59.49v393a4.33 4.33 0 007.37 3.07L260 256 55.37 56.42A4.33 4.33 0 0048 59.49zM345.8 174L89.22 32.64l-.16-.09c-4.42-2.4-8.62 3.58-5 7.06L285.19 234zM84.08 472.39c-3.64 3.48.56 9.46 5 7.06l.16-.09L345.8 338l-60.61-60z" />
    <path d="M449.38 231l-71.65-39.46L310.36 256l67.37 64.46L449.38 281c19.49-10.77 19.49-39.23 0-50z" />
  </svg>
);

// ── Component
export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <FooterEl>
      <Grain />
      <Orb s="600px" c="rgba(124,58,237,0.2)" bottom="-20%" left="20%" />
      <Orb s="400px" c="rgba(232,121,249,0.1)" bottom="-10%" right="-5%" />

      <Container>
        {/* ── CTA band */}
        <CtaBand>
          <CtaEyebrow>Join the waitlist</CtaEyebrow>
          <CtaHeadline>
            Your next chapter
            <br />
            starts with a <em>swipe.</em>
          </CtaHeadline>
          <AppStoreBtns>
            <AppStorePrimary
              href="https://apps.apple.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StoreBtnIcon style={{ color: "#000" }}>
                <AppleSvg />
              </StoreBtnIcon>
              <StoreBtnCopy>
                <StoreBtnTop dark>Download on the</StoreBtnTop>
                <StoreBtnBottom dark>App Store</StoreBtnBottom>
              </StoreBtnCopy>
            </AppStorePrimary>
          </AppStoreBtns>
        </CtaBand>

        {/* ── Footer body */}
        <FooterBody>
          <BrandCol>
            <LogoImg src={logo} alt="Logo" />
            <BrandTagline>
              Real attraction. Real people. Built for those who trust their
              instincts.
            </BrandTagline>
            <SocialsRow>
              {SOCIALS.map((s) => (
                <SocialBtn
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                >
                  {s.icon}
                </SocialBtn>
              ))}
            </SocialsRow>
          </BrandCol>

          {NAV_COLS.map((col) => (
            <NavCol key={col.title}>
              <NavColTitle>{col.title}</NavColTitle>
              <NavList>
                {col.links.map((link) => (
                  <NavItem key={link.label}>
                    <NavLink href={link.href}>{link.label}</NavLink>
                  </NavItem>
                ))}
              </NavList>
            </NavCol>
          ))}
        </FooterBody>

        {/* ── Bottom bar */}
        <BottomBar>
          <Copyright>© {year} YourApp. All rights reserved.</Copyright>
          <LegalLinks>
            <LegalLink href="/privacy">Privacy Policy</LegalLink>
            <LegalLink href="/terms">Terms of Use</LegalLink>
            <LegalLink href="/cookies">Cookie Policy</LegalLink>
          </LegalLinks>
          <MadeWith>
            Made with <span>♥</span> for real connections
          </MadeWith>
        </BottomBar>
      </Container>
    </FooterEl>
  );
}
